import useFieldApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-field-api';
import useFormApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-form-api';
import { SelectIcon } from '@td-client-frontend/icons';
import clsx from 'clsx';
import _ from 'lodash';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEventListener, useClickOutside, useHover } from '../../hooks';
import { isRequiredValidation } from '../../utils';
import { states, provinces } from '../../utils/locations';
import { FieldWrapper } from './FieldWrapper';
import { TextInput } from './TextField';
import { PageSpinner } from '../Spinner';

/**
 * predefined options for DDF
 */
const OPTIONS_ASSETS = {
  states,
  provinces
};

const scrollConfig: ScrollIntoViewOptions = {
  block: 'nearest',
  behavior: 'smooth'
};

const maxVisibleOptionQty = 4;

export interface SelectProps {
  name?: string;
  options: { label: string; value: string | number }[];
  value: string | number | null;
  label?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  error?: string;
  isError?: boolean;
  required?: boolean;
  maxVisible?: number;
  labelBtn?: () => void;
  onOpen?: (arg: boolean) => void;
  isDisabled?: boolean;
  fieldLabelClasses?: string;
  errorClassName?: string;
  optionsAsset?: string;
  wrapperClasses?: string;
  labelClasses?: string;
  labelBtnType?: string;
  rightIcon: ReactNode;
  isLoading?: boolean;
}

export function Select({
  name,
  options = [],
  value,
  label = '',
  placeholder = '',
  onChange,
  error = '',
  isError,
  required = false,
  maxVisible = maxVisibleOptionQty,
  rightIcon,
  labelBtn,
  onOpen,
  wrapperClasses,
  labelClasses,
  isDisabled = false,
  fieldLabelClasses,
  errorClassName,
  optionsAsset,
  labelBtnType,
  isLoading = false
}: SelectProps) {
  /**
   * Internal State
   */
  const [isOpen, setIsOpen] = useState(false);
  const [shouldFilter, setShouldFilter] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [hovered, setHovered] = useState(-1);
  const [ref, isHovering] = useHover();

  useEffect(() => {
    if (onOpen) {
      onOpen(isOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  /**
   * Refs
   */
  const listRef = useRef<HTMLUListElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const itemHeight = _.get(inputRef, 'current?.offsetHeight', 48);
  /**
   * Filter items by search term
   */
  const filtered = useMemo(() => {
    if (!searchText || !shouldFilter) {
      return options;
    }
    if (optionsAsset === 'states') {
      return options.filter(({ label }) => label.toLowerCase().startsWith(searchText.toLowerCase()));
    } else {
      return options.filter(({ label }) => label.toLowerCase().includes(searchText.toLowerCase()));
    }
  }, [options, optionsAsset, searchText, shouldFilter]);

  useEffect(() => {
    if (isOpen && process.env.NODE_ENV !== 'test') {
      dropdownRef.current?.scrollIntoView(scrollConfig);
    }
  });

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    if (!value) {
      // if selected value is messed in a search box, reset text field on closing drop down
      setSearchText('');
    }
  }, [value]);

  const selectItem = useCallback(
    ({ value, label }) => {
      onChange(value);
      setIsOpen(false);
      setSearchText(label);
      setShouldFilter(false);
      setHovered(-1);
    },
    [onChange]
  );

  const validateDisabledStatus = (): void | null => {
    if (isDisabled) return null;
    !isOpen && setShouldFilter(false);
    setIsOpen(!isOpen);
  };

  const handleKeyDown = useCallback(
    (event) => {
      const { keyCode } = event;
      let next;
      switch (keyCode) {
        case 40: // down
          next = hovered + 1;
          if (next < filtered.length) {
            setHovered(next);
            listRef.current!.children[next].scrollIntoView(scrollConfig);
            if (!isOpen) {
              setIsOpen(true);
            }
          }
          break;
        case 38: // up
          next = hovered - 1;
          if (next >= 0) {
            listRef.current!.children[next].scrollIntoView(scrollConfig);
            setHovered(next);
          }
          break;
        case 27: // esc
          if (isOpen) {
            event.stopPropagation();
          }
          setIsOpen(false);
          break;
        case 13: // enter
          event.preventDefault(); // don't submit the form by accident
          if (filtered[hovered]) {
            selectItem(filtered[hovered]);
          }
          break;
        case 9: // tab
          setIsOpen(false);
          setHovered(-1);
          if (!value) {
            setSearchText('');
          }
          break;
        case 35: // end
          event.preventDefault();
          next = filtered.length - 1;
          setHovered(next);
          listRef.current!.children[next].scrollIntoView(scrollConfig);
          break;
        case 36: // home
          event.preventDefault();
          setHovered(0);
          listRef.current!.children[0].scrollIntoView(scrollConfig);
          break;
        default:
      }
    },
    [hovered, filtered, isOpen, listRef, selectItem, value]
  );
  /**
   * Change value of the search box to a corresponding selected value's label
   */

  useEffect(() => {
    if (value) {
      // Not my code but adding and ignore checks around code for optionItem
      // @ts-ignore
      setSearchText(options?.find(({ value: optionValue }) => optionValue === value)?.label || value?.[0]?.code || '');
    } else {
      setSearchText('');
    }
  }, [value, options, setSearchText]);
  /**
   * Other hooks
   */
  useEventListener('keydown', handleKeyDown, inputRef.current);
  useClickOutside(listRef, closeMenu);

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setHovered(-1);
      setSearchText(value);
      setShouldFilter(true);
      setIsOpen(true);
      onChange(''); // invalidate previously selected value
    },
    [onChange]
  );

  return (
    <FieldWrapper
      data-testid='select-wrapper'
      label={label}
      error={error}
      isError={isError}
      name={name}
      labelBtn={labelBtn}
      labelBtnType={labelBtnType}
      className={wrapperClasses}
      labelClasses={labelClasses}
      fieldLabelClasses={fieldLabelClasses}
      errorClassName={errorClassName}
    >
      <div ref={ref} className={clsx('relative', isOpen ? 'z-20' : 'z-10', isHovering && 'z-50')}>
        <TextInput
          data-testid='select-text-input'
          name={name}
          placeholder={placeholder}
          dropdown
          onClick={validateDisabledStatus}
          disabled={isDisabled}
          value={searchText}
          onChange={handleOnChange}
          ref={inputRef}
          Right={
            rightIcon || (
              <SelectIcon
                className={clsx('mr-2 cursor-pointer', isOpen && 'transform rotate-180')}
                onClick={validateDisabledStatus}
                // eslint-disable-next-line react/jsx-closing-bracket-location
              />
            )
          }
          isError={isError}
          wrapperClassName={clsx('relative z-20', isDisabled && 'bg-gray-10')}
          // hack to prevent annoying autofill on Chrome
          // which covers the list of options when the field is empty
          // https://stackoverflow.com/questions/15738259/disabling-chrome-autofill
          required={required}
          autoComplete='off'
          aria-haspopup
          aria-expanded={isOpen}
          aria-activedescendant={`option-for-${value}`}
          aria-owns={`listbox-${name}`}
          aria-required={required}
          aria-invalid={isError}
          aria-describedby={`error-for-${name}`}
          role='combobox'
        />
        <div
          ref={dropdownRef}
          className={clsx(
            'w-full pointer-events-none overflow-hidden shadow-menu absolute z-50 mt-3 rounded-lg',
            isOpen || 'invisible'
          )}
          style={{
            transform: 'translateY(-1rem)',
            width: 'max-content',
            minWidth: '100%'
          }}
        >
          <ul
            data-testid='select-list'
            // @ts-ignore
            ref={listRef}
            tabIndex={-1}
            // @ts-ignore
            expanded={isOpen.toString()}
            selected={searchText}
            className='mb-0 overflow-auto bg-white border-t-0 pointer-events-auto'
            style={{ maxHeight: `${maxVisible * itemHeight}px` }}
            role='listbox'
            id={`listbox-${name}`}
            aria-label={label}
          >
            {filtered.length > 0 ? (
              filtered.map(({ value: optionValue, label }, index) => (
                <li
                  data-testid='select-option'
                  id={`option-for-${optionValue}`}
                  key={`option-${optionValue}-${index}-${label}`}
                  onClick={() => selectItem({ value: optionValue, label })}
                  onKeyDown={() => selectItem({ value: optionValue, label })}
                  className={clsx(
                    'text-black-default cursor-pointer font-effra p-3 border-transparent',
                    'hover:bg-gold hover:border-primary-500 hover:shadow-dd-item-active',
                    'focus:outline-none focus-visible:ring',
                    optionValue === value
                      ? 'bg-gold border-t border-b border-primary-500 shadow-dd-item-active pl-4'
                      : 'border-l-4',
                    index === hovered && 'bg-gold border-primary-500 shadow-dd-item-active'
                  )}
                  role='option'
                  aria-selected={optionValue === searchText}
                  tabIndex={-1}
                >
                  {label}
                </li>
              ))
            ) : (
              <li
                data-testid='select-option'
                className='p-3 border-l-4 border-transparent text-black-default font-effra'
              >
                {isLoading ? <PageSpinner /> : 'No items'}
              </li>
            )}
          </ul>
        </div>
      </div>
    </FieldWrapper>
  );
}
/* istanbul ignore next */ /* this code is not used or will be deleted in other branches */
export function SelectDdf(props: any) {
  const {
    input,
    meta: { error, modified },
    ...rest
  } = useFieldApi(props);
  const { getState } = useFormApi();
  // TODO what is _isOpen used for, and why set it?
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isOpen, setIsOpen] = useState<boolean>(false); // an additional state to set on dropdown open just to adjust the options list (commonDataSetValueAccessors property)

  // Validation
  const isError = modified && error && typeof error === 'string';
  const isRequired = isRequiredValidation(props.validate);

  // Prop Options otherwise State Options
  // @ts-ignore
  let options = props.options || OPTIONS_ASSETS[props.optionsAsset] || [];

  // Filters the options list based on the already selected values ??
  if (props.commonDataSetValueAccessors?.length) {
    const currentValues = getState().values;
    const alreadyUsedOptions = props.commonDataSetValueAccessors.map((accessor: string) =>
      _.get(currentValues, accessor, undefined)
    );
    options = options.filter(({ value }: any) => !alreadyUsedOptions.includes(value));
  }
  options = options.filter(({ readOnly }: any) => !readOnly);

  const handleOnOpen = (isOpen: boolean) => {
    props.commonDataSetValueAccessors?.length && setIsOpen(isOpen);
  };

  return (
    <Select
      {...input}
      onOpen={handleOnOpen}
      options={options}
      isError={isError}
      error={error}
      required={isRequired}
      {...props}
      {...rest}
    />
  );
}
